import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Autocomplete,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Breadcrumbs,
  Link,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';

import { salesOrderService } from '../../../services/salesOrderService';
import { estimateService } from '../../../services/estimateService';
import { CustomerService } from '../../../services/customerService';
import { productService } from '../../../services/productService';
import { customerAddressService, CustomerAddress } from '../../../services/customerAddressService';
import { 
  SalesOrderCreate, 
  SalesOrderUpdate, 
  SalesOrderItem, 
  OtherCharge 
} from '../../../types/salesOrder';
import { formatCurrency } from '../../../utils/formatters';

interface SalesOrderFormData extends Omit<SalesOrderCreate, 'items'> {
  items: SalesOrderItem[];
}

const SalesOrderForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const isEditing = Boolean(id);

  // Check if we're converting from an estimate
  const estimateId = new URLSearchParams(location.search).get('estimate_id');
  const isConvertingFromEstimate = Boolean(estimateId);

  // Form state
  const [formData, setFormData] = useState<SalesOrderFormData>({
    customer_id: '',
    order_date: format(new Date(), 'yyyy-MM-dd'),
    expected_delivery_date: format(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'), // 7 days from now
    billing_address_id: '',
    shipping_address_id: '',
    shipping_charges: 0,
    packing_charges: 0,
    other_charges: [],
    notes: '',
    items: []
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [customerAddresses, setCustomerAddresses] = useState<CustomerAddress[]>([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [salesOrder, setSalesOrder] = useState<any>(null);
  const [salesOrderLoading, setSalesOrderLoading] = useState(false);
  const [customersData, setCustomersData] = useState<any>(null);
  const [productsData, setProductsData] = useState<any>(null);
  const [sourceEstimate, setSourceEstimate] = useState<any>(null);
  const [stockInfo, setStockInfo] = useState<Record<string, any>>({});  // Store stock info by variant_id

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch customers
        console.log('[SalesOrderForm] Fetching customers...');
        const customers = await CustomerService.getCustomers({ page: 1, per_page: 100 });
        console.log('[SalesOrderForm] Customers fetched:', customers);
        console.log('[SalesOrderForm] Customer count:', customers?.items?.length || 0);
        setCustomersData(customers);

        // Fetch products
        const products = await productService.listProducts({ page: 1, per_page: 1000 });
        setProductsData(products);

        // If converting from estimate, fetch estimate data
        if (isConvertingFromEstimate && estimateId) {
          const estimateData = await estimateService.getEstimate(estimateId);
          setSourceEstimate(estimateData);
          
          // Pre-populate form with estimate data
          setFormData({
            customer_id: estimateData.customer_id,
            order_date: format(new Date(), 'yyyy-MM-dd'),
            expected_delivery_date: format(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
            billing_address_id: estimateData.billing_address_id,
            shipping_address_id: estimateData.shipping_address_id,
            shipping_charges: 0,
            packing_charges: 0,
            other_charges: [],
            notes: estimateData.notes || '',
            items: estimateData.items.map((item: any) => ({
              variant_id: item.variant_id,
              quantity: item.quantity,
              unit_price: item.unit_price,
              discount_percent: item.discount_percent,
              description: item.description || '',
              hsn_code: item.hsn_code || ''
            }))
          });
          
          // Fetch stock info for all items
          estimateData.items.forEach((item: any) => {
            if (item.variant_id) {
              fetchStockInfo(item.variant_id);
            }
          });
        }

        // Fetch existing sales order if editing
        if (isEditing && id) {
          setSalesOrderLoading(true);
          const salesOrderData = await salesOrderService.getSalesOrder(id);
          setSalesOrder(salesOrderData);
          setFormData({
            customer_id: salesOrderData.customer_id,
            order_date: salesOrderData.order_date,
            expected_delivery_date: salesOrderData.expected_delivery_date || '',
            billing_address_id: salesOrderData.billing_address_id,
            shipping_address_id: salesOrderData.shipping_address_id,
            shipping_charges: salesOrderData.shipping_charges,
            packing_charges: salesOrderData.packing_charges,
            other_charges: salesOrderData.other_charges,
            notes: salesOrderData.notes || '',
            items: salesOrderData.items
          });
          
          // Fetch stock info for all items
          salesOrderData.items.forEach((item: any) => {
            if (item.variant_id) {
              fetchStockInfo(item.variant_id);
            }
          });
          
          setSalesOrderLoading(false);
        }
      } catch (error: any) {
        console.error('[SalesOrderForm] Failed to fetch data:', error);
        console.error('[SalesOrderForm] Error details:', {
          message: (error as Error)?.message,
          response: (error as any)?.response?.data,
          status: (error as any)?.response?.status
        });
        enqueueSnackbar('Failed to load data', { variant: 'error' });
      }
    };

    fetchData();
  }, [id, isEditing, estimateId, isConvertingFromEstimate, enqueueSnackbar]);

  // Load customer addresses when customer is selected
  useEffect(() => {
    const loadCustomerAddresses = async () => {
      if (formData.customer_id && customersData?.items) {
        const customer = customersData.items.find((c: any) => c.id === formData.customer_id);
        if (customer) {
          setSelectedCustomer(customer);
          setAddressesLoading(true);
          
          try {
            const addressResponse = await customerAddressService.getCustomerAddresses(formData.customer_id);
            setCustomerAddresses(addressResponse.addresses);
            
            // Auto-select default addresses if not already set (only for new orders)
            if (!isEditing && !isConvertingFromEstimate) {
              if (!formData.billing_address_id) {
                const defaultBilling = addressResponse.addresses.find(
                  addr => (addr.address_type === 'billing' || addr.address_type === 'both') && addr.is_default
                );
                if (defaultBilling) {
                  setFormData(prev => ({ ...prev, billing_address_id: defaultBilling.id }));
                }
              }
              
              if (!formData.shipping_address_id) {
                const defaultShipping = addressResponse.addresses.find(
                  addr => (addr.address_type === 'shipping' || addr.address_type === 'both') && addr.is_default
                );
                if (defaultShipping) {
                  setFormData(prev => ({ ...prev, shipping_address_id: defaultShipping.id }));
                }
              }
            }
          } catch (error) {
            console.error('Failed to load customer addresses:', error);
            enqueueSnackbar('Failed to load customer addresses', { variant: 'error' });
          } finally {
            setAddressesLoading(false);
          }
        }
      } else {
        setCustomerAddresses([]);
        setSelectedCustomer(null);
      }
    };

    loadCustomerAddresses();
  }, [formData.customer_id, customersData, isEditing, isConvertingFromEstimate, enqueueSnackbar]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      // Validate form
      const validationErrors: Record<string, string> = {};
      
      if (!formData.customer_id) {
        validationErrors.customer_id = 'Customer is required';
      }
      
      // Addresses are now optional - no validation needed

      if (!formData.items.length) {
        validationErrors.items = 'At least one item is required';
      } else {
        // Validate each item
        formData.items.forEach((item, index) => {
          if (!item.variant_id) {
            validationErrors[`item_${index}_variant`] = 'Product is required';
          }
          if (item.quantity <= 0) {
            validationErrors[`item_${index}_quantity`] = 'Quantity must be greater than 0';
          }
          if (item.unit_price < 0.01) {
            validationErrors[`item_${index}_price`] = 'Unit price must be at least ₹0.01';
          }
        });
      }
      
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        enqueueSnackbar('Please fix the validation errors', { variant: 'error' });
        return;
      }

      if (isEditing) {
        // Clean up form data - convert empty strings to undefined for optional UUID fields
        const cleanedFormData = {
          ...formData,
          billing_address_id: formData.billing_address_id && formData.billing_address_id.trim() !== '' ? formData.billing_address_id : undefined,
          shipping_address_id: formData.shipping_address_id && formData.shipping_address_id.trim() !== '' ? formData.shipping_address_id : undefined,
          expected_delivery_date: formData.expected_delivery_date && formData.expected_delivery_date.trim() !== '' ? formData.expected_delivery_date : undefined,
        };
        
        await salesOrderService.updateSalesOrder(id!, cleanedFormData as SalesOrderUpdate);
        enqueueSnackbar('Sales order updated successfully', { variant: 'success' });
      } else if (isConvertingFromEstimate && estimateId) {
        // Use the conversion endpoint
        const conversionData = {
          estimate_id: estimateId,
          order_date: formData.order_date,
          expected_delivery_date: formData.expected_delivery_date && formData.expected_delivery_date.trim() !== '' ? formData.expected_delivery_date : undefined,
          shipping_charges: formData.shipping_charges,
          packing_charges: formData.packing_charges,
          other_charges: formData.other_charges,
          notes: formData.notes
        };
        const result = await salesOrderService.convertEstimateToSalesOrder(conversionData);
        enqueueSnackbar('Sales order created from estimate successfully', { variant: 'success' });
        navigate(`/admin/documents/sales-orders/${result.sales_order_id}`);
      } else {
        // Clean up form data - convert empty strings to undefined for optional UUID fields
        const cleanedFormData = {
          ...formData,
          billing_address_id: formData.billing_address_id && formData.billing_address_id.trim() !== '' ? formData.billing_address_id : undefined,
          shipping_address_id: formData.shipping_address_id && formData.shipping_address_id.trim() !== '' ? formData.shipping_address_id : undefined,
          expected_delivery_date: formData.expected_delivery_date && formData.expected_delivery_date.trim() !== '' ? formData.expected_delivery_date : undefined,
          // Ensure all numeric fields in items are valid numbers
          items: formData.items.map(item => {
            const quantity = Number(item.quantity);
            const unitPrice = Number(item.unit_price);
            const discountPercent = Number(item.discount_percent);
            
            return {
              ...item,
              quantity: isNaN(quantity) || quantity <= 0 ? 1 : quantity,
              unit_price: isNaN(unitPrice) || unitPrice < 0 ? 0 : unitPrice,
              discount_percent: isNaN(discountPercent) || discountPercent < 0 ? 0 : (discountPercent > 100 ? 100 : discountPercent)
            };
          })
        };
        
        console.log('Submitting sales order with cleaned data:', cleanedFormData);
        
        const result = await salesOrderService.createSalesOrder(cleanedFormData);
        enqueueSnackbar('Sales order created successfully', { variant: 'success' });
        navigate(`/admin/documents/sales-orders/${result.id}`);
      }
    } catch (error: any) {
      console.error('Failed to save sales order:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to save sales order';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle adding new line item
  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          variant_id: '',
          quantity: 1,
          unit_price: 0,
          discount_percent: 0,
          description: '',
          hsn_code: ''
        }
      ]
    }));
  };

  // Fetch stock info for a variant
  const fetchStockInfo = async (variantId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/inventory/stock?search=${variantId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.items && data.items.length > 0) {
          const stock = data.items[0];
          setStockInfo(prev => ({
            ...prev,
            [variantId]: {
              current_stock: stock.current_stock,
              reserved_stock: stock.reserved_stock,
              available_stock: stock.available_stock,
              is_low_stock: stock.is_low_stock
            }
          }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch stock info:', error);
    }
  };

  // Handle removing line item
  const removeLineItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Handle line item changes
  const updateLineItem = (index: number, field: keyof SalesOrderItem, value: any) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
    
    // Clear item-specific errors when user makes changes
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`item_${index}_variant`];
      delete newErrors[`item_${index}_quantity`];
      delete newErrors[`item_${index}_price`];
      return newErrors;
    });
  };

  // Handle adding other charges
  const addOtherCharge = () => {
    setFormData(prev => ({
      ...prev,
      other_charges: [
        ...(prev.other_charges || []),
        { name: '', amount: 0, description: '' }
      ]
    }));
  };

  // Handle removing other charges
  const removeOtherCharge = (index: number) => {
    setFormData(prev => ({
      ...prev,
      other_charges: (prev.other_charges || []).filter((_, i) => i !== index)
    }));
  };

  // Handle other charge changes
  const updateOtherCharge = (index: number, field: keyof OtherCharge, value: any) => {
    setFormData(prev => ({
      ...prev,
      other_charges: (prev.other_charges || []).map((charge, i) => 
        i === index ? { ...charge, [field]: value } : charge
      )
    }));
  };

  // Calculate totals
  const calculateTotals = () => {
    const itemsSubtotal = formData.items.reduce((sum, item) => {
      const lineTotal = item.quantity * item.unit_price;
      const discountAmount = lineTotal * (item.discount_percent / 100);
      return sum + (lineTotal - discountAmount);
    }, 0);

    const otherChargesTotal = (formData.other_charges || []).reduce((sum, charge) => sum + charge.amount, 0);
    const subtotal = itemsSubtotal + (formData.shipping_charges || 0) + (formData.packing_charges || 0) + otherChargesTotal;
    const taxAmount = subtotal * 0.18; // 18% GST
    const total = subtotal + taxAmount;

    return { itemsSubtotal, otherChargesTotal, subtotal, taxAmount, total };
  };

  const { itemsSubtotal, otherChargesTotal, subtotal, taxAmount, total } = calculateTotals();

  // Check if any items have insufficient stock
  const hasInsufficientStock = formData.items.some(item => 
    item.variant_id && 
    stockInfo[item.variant_id] && 
    item.quantity > stockInfo[item.variant_id].available_stock
  );

  const insufficientStockItems = formData.items.filter(item => 
    item.variant_id && 
    stockInfo[item.variant_id] && 
    item.quantity > stockInfo[item.variant_id].available_stock
  );

  // Clear errors when form data changes
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const newErrors = { ...errors };
      
      // Clear customer error if customer is selected
      if (formData.customer_id && newErrors.customer_id) {
        delete newErrors.customer_id;
      }
      
      // Clear address errors if addresses are selected
      if (formData.billing_address_id && newErrors.billing_address_id) {
        delete newErrors.billing_address_id;
      }
      
      if (formData.shipping_address_id && newErrors.shipping_address_id) {
        delete newErrors.shipping_address_id;
      }
      
      // Clear items error if items exist
      if (formData.items.length > 0 && newErrors.items) {
        delete newErrors.items;
      }
      
      setErrors(newErrors);
    }
  }, [formData.customer_id, formData.billing_address_id, formData.shipping_address_id, formData.items.length, errors]);

  if (salesOrderLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" href="/admin/dashboard">
          Dashboard
        </Link>
        <Link color="inherit" href="/admin/documents/sales-orders">
          Sales Orders
        </Link>
        <Typography color="text.primary">
          {isEditing ? 'Edit Sales Order' : isConvertingFromEstimate ? 'Convert Estimate to Sales Order' : 'New Sales Order'}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/admin/documents/sales-orders')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4">
              {isEditing ? `Edit Sales Order ${salesOrder?.order_number}` : 
               isConvertingFromEstimate ? 'Convert Estimate to Sales Order' : 
               'Create New Sales Order'}
            </Typography>
            {isConvertingFromEstimate && sourceEstimate && (
              <Box mt={1}>
                <Chip 
                  label={`Converting from Estimate ${sourceEstimate.estimate_number}`}
                  color="info"
                  size="small"
                />
              </Box>
            )}
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Sales Order'}
        </Button>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={1.5}>
          {/* Stock Warning Alert */}
          {hasInsufficientStock && (
            <Grid item xs={12}>
              <Alert severity="warning" sx={{ mb: 0 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  ⚠ Insufficient Stock Warning
                </Typography>
                <Typography variant="body2">
                  Some items exceed available stock. Order can be created as DRAFT but cannot be confirmed until stock is adjusted or quantities are reduced.
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {insufficientStockItems.map((item, idx) => {
                    const variant = productsData?.items?.flatMap((p: any) => 
                      (p.variants || []).map((v: any) => ({
                        ...v,
                        product_name: p.name
                      }))
                    ).find((v: any) => v.id === item.variant_id);
                    
                    return (
                      <Typography key={idx} variant="caption" display="block">
                        • {variant?.product_name || 'Unknown'} - {variant?.name || 'Unknown'}: 
                        Requested {item.quantity}, Available {stockInfo[item.variant_id]?.available_stock || 0}
                      </Typography>
                    );
                  })}
                </Box>
              </Alert>
            </Grid>
          )}
          
          {/* Customer Information - Single Row */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <Autocomplete
                      options={customersData?.items || []}
                      getOptionLabel={(option: any) => {
                        // Try multiple fields to find a display name
                        return option.business_name || 
                               option.billing_name || 
                               option.contact_person || 
                               option.email || 
                               option.mobile || 
                               'Unnamed Customer';
                      }}
                      getOptionKey={(option: any) => option.id}
                      value={selectedCustomer}
                      onChange={(_, newValue) => {
                        console.log('[SalesOrderForm] Customer selected:', newValue);
                        setSelectedCustomer(newValue);
                        setFormData(prev => ({
                          ...prev,
                          customer_id: newValue?.id || ''
                        }));
                      }}
                      disabled={isConvertingFromEstimate}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Customer"
                          size="small"
                          required
                          error={!!errors.customer_id}
                          helperText={errors.customer_id}
                          placeholder={customersData?.items?.length ? "Select customer" : "Loading customers..."}
                        />
                      )}
                    />
                    {/* Debug info */}
                    {process.env.NODE_ENV === 'development' && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {customersData ? `${customersData.items?.length || 0} customers loaded` : 'No data'}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Order Date"
                      type="date"
                      value={formData.order_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, order_date: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Expected Delivery"
                      type="date"
                      value={formData.expected_delivery_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, expected_delivery_date: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={1.5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Shipping"
                      type="number"
                      value={formData.shipping_charges}
                      onChange={(e) => setFormData(prev => ({ ...prev, shipping_charges: parseFloat(e.target.value) || 0 }))}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={1.5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Packing"
                      type="number"
                      value={formData.packing_charges}
                      onChange={(e) => setFormData(prev => ({ ...prev, packing_charges: parseFloat(e.target.value) || 0 }))}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      onClick={() => setFormData(prev => ({ 
                        ...prev, 
                        billing_address_id: '', 
                        shipping_address_id: '' 
                      }))}
                    >
                      Clear Addresses
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Addresses - Collapsible/Optional */}
          {formData.customer_id && (
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Billing Address (Optional)</InputLabel>
                        <Select
                          value={formData.billing_address_id}
                          onChange={(e) => setFormData(prev => ({ ...prev, billing_address_id: e.target.value }))}
                          label="Billing Address (Optional)"
                          disabled={addressesLoading}
                        >
                          <MenuItem value="">None</MenuItem>
                          {customerAddresses.filter(addr => addr.address_type === 'billing' || addr.address_type === 'both').map((address) => (
                            <MenuItem key={address.id} value={address.id}>
                              {address.address_line1}, {address.city}, {address.state} - {address.postal_code}
                              {address.is_default && ' (Default)'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Shipping Address (Optional)</InputLabel>
                        <Select
                          value={formData.shipping_address_id}
                          onChange={(e) => setFormData(prev => ({ ...prev, shipping_address_id: e.target.value }))}
                          label="Shipping Address (Optional)"
                          disabled={addressesLoading}
                        >
                          <MenuItem value="">None</MenuItem>
                          {customerAddresses.filter(addr => addr.address_type === 'shipping' || addr.address_type === 'both').map((address) => (
                            <MenuItem key={address.id} value={address.id}>
                              {address.address_line1}, {address.city}, {address.state} - {address.postal_code}
                              {address.is_default && ' (Default)'}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Line Items */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Line Items ({formData.items.length})
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addLineItem}
                    sx={{ minWidth: 'auto', px: 1.5 }}
                  >
                    Add
                  </Button>
                </Box>

                {errors.items && (
                  <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
                    {errors.items}
                  </Alert>
                )}

                <TableContainer sx={{ maxHeight: '450px', overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '40px' }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', minWidth: '200px' }}>Product</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '80px' }}>MRP</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '70px' }}>Qty</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '90px' }}>Unit Price</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '70px' }}>Disc %</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '90px' }}>Taxable</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '70px' }}>Tax %</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '90px' }}>Tax Amt</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '100px' }}>Amount</TableCell>
                        <TableCell sx={{ fontWeight: 600, py: 0.75, fontSize: '0.75rem', width: '50px' }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.items.map((item, index) => {
                        const lineTotal = item.quantity * item.unit_price;
                        const discountAmount = lineTotal * (item.discount_percent / 100);
                        const taxableAmount = lineTotal - discountAmount;
                        const taxRate = 18; // Default GST rate
                        const taxAmount = taxableAmount * (taxRate / 100);
                        const finalAmount = taxableAmount + taxAmount;
                        
                        // Get selected variant to show MRP
                        const selectedVariant = productsData?.items?.flatMap((p: any) => 
                          (p.variants || []).map((v: any) => ({
                            ...v,
                            product_name: p.name,
                            product_id: p.id
                          }))
                        ).find((v: any) => v.id === item.variant_id);

                        return (
                          <TableRow key={index} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                            <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                              {index + 1}
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <Autocomplete
                                options={productsData?.items?.flatMap((p: any) => 
                                  (p.variants || []).map((v: any) => ({
                                    ...v,
                                    product_name: p.name,
                                    product_id: p.id,
                                    hsn_code: p.hsn_code  // HSN code is on product, not variant
                                  }))
                                ) || []}
                                getOptionLabel={(option: any) => {
                                  const stockAvailable = stockInfo[option.id]?.available_stock;
                                  const stockText = stockAvailable !== undefined ? ` (${stockAvailable} available)` : '';
                                  return `${option.product_name || 'Unknown'} - ${option.name}${stockText}`;
                                }}
                                getOptionKey={(option: any) => option.id}
                                value={selectedVariant || null}
                                onChange={(_, newValue) => {
                                  if (newValue) {
                                    // Auto-populate unit price from sale_price (or fallback to mrp)
                                    const unitPrice = Number(newValue.sale_price || newValue.mrp || 0);
                                    
                                    // Fetch stock info for this variant
                                    fetchStockInfo(newValue.id);
                                    
                                    // Update all fields at once to avoid state batching issues
                                    setFormData(prev => ({
                                      ...prev,
                                      items: prev.items.map((item, i) => 
                                        i === index ? {
                                          ...item,
                                          variant_id: newValue.id,
                                          unit_price: unitPrice,
                                          hsn_code: newValue.hsn_code || '',  // Now includes product's HSN
                                          discount_percent: Number(item.discount_percent) || 0
                                        } : item
                                      )
                                    }));
                                  } else {
                                    updateLineItem(index, 'variant_id', '');
                                    updateLineItem(index, 'unit_price', 0);
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    size="small"
                                    placeholder="Select product"
                                    error={!!errors[`item_${index}_variant`]}
                                    sx={{ 
                                      minWidth: 200,
                                      '& .MuiInputBase-root': { fontSize: '0.8125rem', py: 0.25 },
                                      '& .MuiInputBase-input': { py: '4px !important' }
                                    }}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                              {selectedVariant?.mrp ? `₹${selectedVariant.mrp}` : '-'}
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <Box>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={item.quantity}
                                  onChange={(e) => {
                                    const value = parseInt(e.target.value);
                                    updateLineItem(index, 'quantity', isNaN(value) || value < 1 ? 1 : value);
                                  }}
                                  inputProps={{ min: 1 }}
                                  sx={{ 
                                    width: 65,
                                    '& .MuiInputBase-input': { fontSize: '0.8125rem', py: 0.5, px: 0.75 }
                                  }}
                                  error={!!errors[`item_${index}_quantity`] || (item.variant_id && stockInfo[item.variant_id] && item.quantity > stockInfo[item.variant_id].available_stock)}
                                />
                                {item.variant_id && stockInfo[item.variant_id] && (
                                  <Box sx={{ mt: 0.5 }}>
                                    {item.quantity > stockInfo[item.variant_id].available_stock ? (
                                      <Chip 
                                        label={`⚠ Only ${stockInfo[item.variant_id].available_stock} available`}
                                        size="small"
                                        color="error"
                                        sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                                      />
                                    ) : stockInfo[item.variant_id].is_low_stock ? (
                                      <Chip 
                                        label={`${stockInfo[item.variant_id].available_stock} in stock`}
                                        size="small"
                                        color="warning"
                                        sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                                      />
                                    ) : (
                                      <Chip 
                                        label={`${stockInfo[item.variant_id].available_stock} available`}
                                        size="small"
                                        color="success"
                                        sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                                      />
                                    )}
                                  </Box>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <TextField
                                type="number"
                                size="small"
                                value={item.unit_price}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value);
                                  updateLineItem(index, 'unit_price', isNaN(value) || value < 0 ? 0 : value);
                                }}
                                inputProps={{ min: 0, step: 0.01 }}
                                sx={{ 
                                  width: 85,
                                  '& .MuiInputBase-input': { fontSize: '0.8125rem', py: 0.5, px: 0.75 }
                                }}
                                error={!!errors[`item_${index}_price`]}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <TextField
                                type="number"
                                size="small"
                                value={item.discount_percent}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value);
                                  let discount = isNaN(value) ? 0 : value;
                                  // Clamp between 0 and 100
                                  discount = Math.max(0, Math.min(100, discount));
                                  updateLineItem(index, 'discount_percent', discount);
                                }}
                                inputProps={{ min: 0, max: 100, step: 0.01 }}
                                sx={{ 
                                  width: 65,
                                  '& .MuiInputBase-input': { fontSize: '0.8125rem', py: 0.5, px: 0.75 }
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.8125rem', fontWeight: 500 }}>
                              ₹{taxableAmount.toFixed(2)}
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
                              {taxRate}%
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.8125rem', color: 'info.main' }}>
                              ₹{taxAmount.toFixed(2)}
                            </TableCell>
                            <TableCell sx={{ py: 0.5, fontSize: '0.8125rem', fontWeight: 600, color: 'primary.main' }}>
                              ₹{finalAmount.toFixed(2)}
                            </TableCell>
                            <TableCell sx={{ py: 0.5 }}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeLineItem(index)}
                                disabled={formData.items.length === 1}
                                sx={{ p: 0.5 }}
                              >
                                <DeleteIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Calculation Summary */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                {/* Calculation Preview - Compact */}
                <Box>
                  <Divider sx={{ mb: 1.5 }} />
                  <Grid container spacing={1.5} justifyContent="flex-end">
                    <Grid item xs={12} md={5}>
                      <Card variant="outlined">
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2">Items Subtotal:</Typography>
                            <Typography variant="body2">{formatCurrency(itemsSubtotal)}</Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2">Shipping:</Typography>
                            <Typography variant="body2">{formatCurrency(formData.shipping_charges || 0)}</Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2">Packing:</Typography>
                            <Typography variant="body2">{formatCurrency(formData.packing_charges || 0)}</Typography>
                          </Box>
                          {otherChargesTotal > 0 && (
                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                              <Typography variant="body2">Other Charges:</Typography>
                              <Typography variant="body2">{formatCurrency(otherChargesTotal)}</Typography>
                            </Box>
                          )}
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2">Taxable Amount:</Typography>
                            <Typography variant="body2">{formatCurrency(subtotal)}</Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="body2">GST (18%):</Typography>
                            <Typography variant="body2">{formatCurrency(taxAmount)}</Typography>
                          </Box>
                          <Divider sx={{ my: 0.75 }} />
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="subtitle1" fontWeight={600} color="primary">
                              Total Amount:
                            </Typography>
                            <Typography variant="subtitle1" fontWeight={600} color="primary">
                              {formatCurrency(total)}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            Items: {formData.items.length} | Total Qty: {formData.items.reduce((sum, item) => sum + item.quantity, 0)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Other Charges - Compact */}
          {(formData.other_charges || []).length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Other Charges ({(formData.other_charges || []).length})
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={addOtherCharge}
                      sx={{ minWidth: 'auto', px: 1.5 }}
                    >
                      Add
                    </Button>
                  </Box>
                  {(formData.other_charges || []).map((charge, index) => (
                    <Grid container spacing={1} key={index} sx={{ mb: 1 }}>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Name"
                          value={charge.name}
                          onChange={(e) => updateOtherCharge(index, 'name', e.target.value)}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          label="Amount"
                          type="number"
                          value={charge.amount}
                          onChange={(e) => updateOtherCharge(index, 'amount', parseFloat(e.target.value) || 0)}
                          inputProps={{ min: 0, step: 0.01 }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Description"
                          value={charge.description || ''}
                          onChange={(e) => updateOtherCharge(index, 'description', e.target.value)}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={1}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeOtherCharge(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}
          {(formData.other_charges || []).length === 0 && (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={addOtherCharge}
                sx={{ ml: 1 }}
              >
                Add Other Charges
              </Button>
            </Grid>
          )}

          {/* Notes - Compact Inline */}
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <TextField
                  fullWidth
                  label="Order Notes"
                  multiline
                  rows={2}
                  size="small"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.8125rem' } }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default SalesOrderForm;